import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Layers, Wrench, Paintbrush2, ScanLine } from 'lucide-react';
import { Button, Spinner } from '@crc-evans/ui-components';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../../constants/ndtPipelineTypeOptions';
import ModernMultiCheckboxSelect from '../common/ModernMultiCheckboxSelect';
import { ndtStationToPayload, ndtTypesFromStationEntry } from '../../utils/sowNdtStation';

const normalizeStationNumbers = (list) =>
  (Array.isArray(list) ? list : [])
    .map((x) => Number(x))
    .filter((n) => !Number.isNaN(n) && n > 0);

const ndtRowsFromApi = (ndtStations) => {
  const raw = Array.isArray(ndtStations) ? ndtStations : [];
  const out = [];
  raw.forEach((entry, idx) => {
    if (entry && typeof entry === 'object') {
      const stationNumber = Number(entry.stationNumber ?? entry.station_number);
      if (Number.isNaN(stationNumber) || stationNumber <= 0) return;
      out.push({
        id: `ndt-${stationNumber}-${idx}`,
        stationNumber,
        ndtTypes: ndtTypesFromStationEntry(entry),
      });
      return;
    }
    const stationNumber = Number(entry);
    if (!Number.isNaN(stationNumber) && stationNumber > 0) {
      out.push({ id: `ndt-${stationNumber}-${idx}`, stationNumber, ndtTypes: [] });
    }
  });
  const unique = Object.values(
    out.reduce((acc, r) => {
      acc[r.stationNumber] = acc[r.stationNumber] || r;
      return acc;
    }, {})
  ).sort((a, b) => a.stationNumber - b.stationNumber);
  return unique;
};

const StationTag = ({ number, onRemove, disabled }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      background: '#EFF6FF',
      border: '1px solid #BFDBFE',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 500,
      color: '#1D4ED8',
    }}
  >
    Station {number}
    {!disabled && (
      <button
        type="button"
        onClick={() => onRemove(number)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 0 0 4px',
          color: '#93C5FD',
          display: 'flex',
          alignItems: 'center',
        }}
        aria-label={`Remove station ${number}`}
      >
        <Trash2 size={12} />
      </button>
    )}
  </span>
);

const StationSection = ({ title, icon: Icon, color, bgColor, stations, onAdd, onRemove, disabled }) => {
  const [inputVal, setInputVal] = useState('');

  const handleAdd = () => {
    const num = inputVal.trim();
    if (!num) return;
    if (isNaN(Number(num))) {
      message.warning('Please enter a valid station number.');
      return;
    }
    const n = Number(num);
    if (stations.includes(n)) {
      message.warning(`Station ${n} is already added.`);
      return;
    }
    onAdd(n);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div
          style={{
            background: bgColor,
            color: color,
            padding: '8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={18} />
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1E293B' }}>{title}</h4>
          <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>
            {stations.length} station{stations.length !== 1 ? 's' : ''} defined
          </p>
        </div>
      </div>

      {!disabled && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <input
            type="number"
            min={1}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Station number"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #D1D5DB',
              fontSize: '14px',
              outline: 'none',
              color: '#1F2937',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
          />
          <button
            type="button"
            onClick={handleAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 14px',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            <Plus size={16} /> Add
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '32px' }}>
        {stations.length === 0 ? (
          <span style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>
            No stations defined yet.
          </span>
        ) : (
          [...stations].sort((a, b) => a - b).map((n) => (
            <StationTag
              key={n}
              number={n}
              onRemove={onRemove}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
};

const DPRSettingsProductionLineTab = ({ selectedProject }) => {
  const [totalStations, setTotalStations] = useState('');
  const [weldingStations, setWeldingStations] = useState([]);
  const [coatingStations, setCoatingStations] = useState([]);
  const [ndtStationRows, setNdtStationRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    axiosConfig
      .get(`/production-line-setup?projectId=${projectId}`)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        if (d) {
          setTotalStations(d.totalStations != null ? String(d.totalStations) : '');
          setWeldingStations(normalizeStationNumbers(d.weldingStations));
          setCoatingStations(normalizeStationNumbers(d.coatingStations));
          setNdtStationRows(ndtRowsFromApi(d.ndtStations));
        }
      })
      .catch(() => {
        /* No setup yet — start fresh */
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) return;
    if (!totalStations || isNaN(Number(totalStations)) || Number(totalStations) < 1) {
      message.warning('Enter a valid total number of stations.');
      return;
    }
    setIsSaving(true);
    try {
      const ndtStations = (Array.isArray(ndtStationRows) ? ndtStationRows : [])
        .filter((r) => !Number.isNaN(Number(r.stationNumber)) && Number(r.stationNumber) > 0)
        .map((r) => ndtStationToPayload(Number(r.stationNumber), r.ndtTypes || []));

      const payload = {
        projectId: Number(projectId),
        totalStations: Number(totalStations),
        weldingStations,
        coatingStations,
        ndtStations,
      };
      await axiosConfig.put('/production-line-setup', payload);
      message.success('Production line setup saved successfully.');
    } catch (err) {
      console.error('Error saving production line setup:', err);
      message.error('Failed to save production line setup.');
    } finally {
      setIsSaving(false);
    }
  };

  const addStation = (setter) => (n) => setter((prev) => [...prev, n]);
  const removeStation = (setter) => (n) => setter((prev) => prev.filter((s) => s !== n));

  const addNdtStation = (n) =>
    setNdtStationRows((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      if (list.some((r) => Number(r.stationNumber) === Number(n))) return prev;
      return [...list, { id: `ndt-${n}-${Date.now()}`, stationNumber: Number(n), ndtTypes: [] }].sort(
        (a, b) => Number(a.stationNumber) - Number(b.stationNumber)
      );
    });

  const removeNdtStation = (n) =>
    setNdtStationRows((prev) =>
      (Array.isArray(prev) ? prev : []).filter((r) => Number(r.stationNumber) !== Number(n))
    );

  const updateNdtStationTypes = (stationNumber, ndtTypes) =>
    setNdtStationRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) =>
        Number(r.stationNumber) === Number(stationNumber)
          ? { ...r, ndtTypes: Array.isArray(ndtTypes) ? ndtTypes : [] }
          : r
      )
    );

  if (!selectedProject) {
    return (
      <div style={{ padding: '24px', color: '#64748B' }}>
        Select a project to configure the production line.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '28px' }}>
        <div
          style={{
            background: '#F0FDF4',
            color: '#16A34A',
            padding: '10px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Layers size={24} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>
            Production Line Set-Up
          </h3>
          <p style={{ margin: '4px 0 0 0', color: '#64748B', fontSize: '14px' }}>
            Define stations, their types, and NDT configuration for this project.
          </p>
        </div>
      </div>

      {/* Total Stations */}
      <div style={{ marginBottom: '24px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '8px',
          }}
        >
          Total Number of Stations <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <input
          type="number"
          min={1}
          value={totalStations}
          onChange={(e) => setTotalStations(e.target.value)}
          placeholder="e.g. 10"
          style={{
            width: '200px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #D1D5DB',
            fontSize: '14px',
            outline: 'none',
            color: '#1F2937',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
          onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
        />
        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#94A3B8' }}>
          Enter the total count, then assign each station below by type.
        </p>
      </div>

      {/* Station Sections */}
      <StationSection
        title="Welding Stations"
        icon={Wrench}
        color="#D97706"
        bgColor="#FFFBEB"
        stations={weldingStations}
        onAdd={addStation(setWeldingStations)}
        onRemove={removeStation(setWeldingStations)}
      />

      <StationSection
        title="Coating Stations"
        icon={Paintbrush2}
        color="#7C3AED"
        bgColor="#F5F3FF"
        stations={coatingStations}
        onAdd={addStation(setCoatingStations)}
        onRemove={removeStation(setCoatingStations)}
      />

      <StationSection
        title="NDT Stations"
        icon={ScanLine}
        color="#0891B2"
        bgColor="#ECFEFF"
        stations={ndtStationRows.map((r) => Number(r.stationNumber))}
        onAdd={addNdtStation}
        onRemove={removeNdtStation}
      />

      {/* NDT Station Details (per station NDT types) */}
      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '16px',
          marginTop: '8px',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
          NDT station details
        </div>
        {ndtStationRows.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>
            Add NDT stations above to configure their NDT types.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {ndtStationRows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                <div style={{ fontSize: '13px', color: '#1F2937', fontWeight: 500 }}>
                  Station {row.stationNumber}
                </div>
                <ModernMultiCheckboxSelect
                  placeholder="Select NDT Type(s)"
                  value={Array.isArray(row.ndtTypes) ? row.ndtTypes : []}
                  options={NDT_PIPELINE_TYPE_OPTIONS}
                  onChange={(vals) => updateNdtStationTypes(row.stationNumber, vals)}
                  disabled={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #F1F5F9',
          marginTop: '8px',
        }}
      >
        <Button
          label={isSaving ? 'Saving...' : 'Save'}
          variant="secondary"
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </div>
  );
};

export default DPRSettingsProductionLineTab;
